/**
 * GWP In-Cart Hydrator.
 * Ensures the GWP in-cart UI initializes and stays updated across dynamic drawer re-renders.
 */

const messageStrings = {
  GROWING: {
    title: 'Unlock A Free Gift',
    status: 'LOCKED',
    cta: 'Locked'
  },
  ELIGIBLE: {
    title: 'Free Gift Unlocked!',
    status: 'UNLOCKED',
    message: '',
    cta: 'Choose a product'
  },
  CLAIMED: {
    title: 'Free Gift Claimed!',
    status: 'CLAIMED',
    message: '',
    cta: 'Change or remove product'
  },
  DEFAULT: {
    title: 'Unlock A Free Gift',
    status: 'LOCKED',
    cta: 'Choose my gift'
  }
};

// Type-specific message strings for collection and page promotions
const collectionMessageStrings = {
  GROWING: {
    title: 'Unlock Special Collections',
    status: 'LOCKED',
    cta: 'Locked'
  },
  ELIGIBLE: {
    title: 'Collections Unlocked!',
    status: 'UNLOCKED',
    message: '',
    cta: 'choose product'
  },
  CLAIMED: {
    title: 'Collection Products Added!',
    status: 'CLAIMED',
    message: '',
    cta: 'choose product'
  },
  DEFAULT: {
    title: 'Unlock Special Collections',
    status: 'LOCKED',
    cta: 'choose product'
  }
};

const pageMessageStrings = {
  GROWING: {
    title: 'Unlock Special Offer',
    status: 'LOCKED',
    cta: 'Locked'
  },
  ELIGIBLE: {
    title: 'Special Offer Unlocked!',
    status: 'UNLOCKED',
    message: '',
    cta: 'View more'
  },
  DEFAULT: {
    title: 'Unlock Special Offer',
    status: 'LOCKED',
    cta: 'View more'
  }
};

/**
 * @param {HTMLElement} container - The root .gwp-incart element.
 */
function hydrateIncart(container) {
  if (!container || container.dataset.gwpHydrated === 'true') return;
  container.dataset.gwpHydrated = 'true';

  const els = {
    container,
    fill: container.querySelector('#gwp-incart-progress-fill'),
    required: container.querySelector('#gwp-incart-required'),
    status: container.querySelector('#gwp-incart-status'),
    message: container.querySelector('#gwp-incart-message'),
    products: container.querySelector('#gwp-incart-products'),
    cta: container.querySelector('#gwp-incart-cta'),
    title: container.querySelector('.gwp-incart__title'),
    prev: container.querySelector('#gwp-incart-prev'),
    next: container.querySelector('#gwp-incart-next'),
    accordionToggle: container.querySelector('#gwp-incart-accordion-toggle'),
    accordionContent: container.querySelector('#gwp-incart-content')
  };

  const placeholder = getThemeAssetUrl('al-icon-diamond.svg');
  const currency = /** @type {any} */ (window).Shopify?.currency?.active || 'GBP';

  /**
   * @param {string} name
   * @returns {string}
   */
  function getThemeAssetUrl(name) {
    // The snippet in Liquid resolves the asset URL; in JS fallback we build a relative path.
    // Adjust if your build pipeline relocates assets.
    return `/cdn/shop/t/` + /** @type {any} */ (window.theme?.id || '') + `/assets/${name}`;
  }

  /**
   * @param {string|null|undefined} str
   */
  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (s) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[s] || s;
    });
  }

  /**
   * @param {number|string} amount
   * @param {string=} code
   * @param {bool} showFree
   */
  function formatMoney(amount, code, showFree = true) {
    if (amount === 0 && showFree) return 'FREE';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: code || currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Number(amount) || 0);
    } catch {
      const n = Number(amount) || 0;
      return (code === 'USD' ? '$' : '£') + n.toFixed(2);
    }
  }

  /**
   * @param {any} state
   */
  function normalizeState(state) {
    if (!state || typeof state !== 'object') return null;
    if (typeof state.giftState !== 'string') return null;
    return state;
  }

  function getSnapshot() {
    try {
      const fg = /** @type {any} */ (window).freeGift || {};
      const st =
        typeof fg.state === 'function'
          ? fg.state()
          : typeof fg.getState === 'function'
            ? fg.getState()
            : fg.snapshot || null;
      return normalizeState(st);
    } catch {
      return null;
    }
  }

  /**
   * Get accordion state from localStorage.
   * @returns {boolean} True if expanded, false if collapsed.
   */
  function getAccordionState() {
    try {
      const stored = localStorage.getItem('gwp-incart-accordion-state');
      return stored !== null ? stored === 'true' : true; // Default to open.
    } catch {
      return true; // Default to open if localStorage fails.
    }
  }

  /**
   * Save accordion state to localStorage.
   * @param {boolean} isExpanded
   */
  function saveAccordionState(isExpanded) {
    try {
      localStorage.setItem('gwp-incart-accordion-state', String(isExpanded));
    } catch {
      // Silently fail if localStorage is not available.
    }
  }

  /**
   * Update accordion visual state.
   * @param {boolean} isExpanded
   */
  function updateAccordionState(isExpanded) {
    if (!els.container || !els.accordionToggle) return;

    // Update container classes.
    els.container.classList.toggle('gwp-incart--expanded', isExpanded);
    els.container.classList.toggle('gwp-incart--collapsed', !isExpanded);

    // Update button aria-expanded.
    els.accordionToggle.setAttribute('aria-expanded', String(isExpanded));

    // Save state to localStorage.
    saveAccordionState(isExpanded);
  }

  /**
   * Toggle accordion state.
   */
  function toggleAccordion() {
    const isCurrentlyExpanded = els.container?.classList.contains('gwp-incart--expanded') ?? true;
    updateAccordionState(!isCurrentlyExpanded);
  }

  /**
   * Keep prev/next button disabled states in sync with swiper.
   * @param {any} swiper
   */
  function syncArrows(swiper) {
    try {
      const element = /** @type {HTMLElement} */ (els.products);
      const slidesLength = Number(swiper?.slides?.length || element?.querySelectorAll('swiper-slide')?.length || 0);
      const perView =
        Number(swiper?.params?.slidesPerView) || parseFloat(element?.getAttribute('slides-per-view') || '1') || 1;
      const activeIndex = typeof swiper?.activeIndex === 'number' ? swiper.activeIndex : 0;

      let atStart = !!swiper?.isBeginning;
      let atEnd = !!swiper?.isEnd;

      // Fallback calculation to handle fractional slidesPerView and web component edge cases.
      if (slidesLength <= perView) {
        atStart = true;
        atEnd = true;
      } else {
        atStart = activeIndex <= 0;
        atEnd = activeIndex >= slidesLength - Math.ceil(perView);
      }

      if (els.prev) {
        /** @type {HTMLElement} */ (els.prev).toggleAttribute('disabled', atStart);
        /** @type {HTMLElement} */ (els.prev).setAttribute('aria-disabled', String(atStart));
      }
      if (els.next) {
        /** @type {HTMLElement} */ (els.next).toggleAttribute('disabled', atEnd);
        /** @type {HTMLElement} */ (els.next).setAttribute('aria-disabled', String(atEnd));
      }
    } catch {}
  }

  /**
   * Get the appropriate message strings based on promotion type.
   * @param {string} promotionType
   * @returns {typeof messageStrings}
   */
  function getMessageStrings(promotionType) {
    if (promotionType === 'Spend X, see collection link(s)') {
      return collectionMessageStrings;
    } else if (promotionType === 'Spend X, see message') {
      return pageMessageStrings;
    }
    return messageStrings;
  }

  /**
   * @param {ReturnType<typeof normalizeState>} state
   */
  function render(state) {
    if (window.gwpSettings.enabled !== true) return;
    const giftState = state && state.giftState;
    const promotionType = state && state.type;

    if (els.container) {
      els.container.hidden = false;
      els.container.removeAttribute('aria-hidden');
      try {
        els.container.classList.remove('gwp-incart--loading');
      } catch {}

      // Remove existing state classes.
      try {
        els.container.classList.remove(
          'gwp-incart--state-growing',
          'gwp-incart--state-eligible',
          'gwp-incart--state-claimed',
          'gwp-incart--state-unavailable'
        );
      } catch {}

      // Add current state class for styling.
      if (giftState) {
        try {
          els.container.classList.add(`gwp-incart--state-${giftState.toLowerCase()}`);
        } catch {}
      }
    }

    const required = Number((state && state.requiredSpend) || 0);
    const current = Number((state && state.currentSpend) || 0);
    const remaining = Math.max(0, required - current);
    const pct = required > 0 ? Math.min(100, Math.round((current / required) * 100)) : 0;

    const strings = getMessageStrings(promotionType)[giftState] || getMessageStrings(promotionType).DEFAULT;
    let headerTitle = state?.title || strings.title;
    let statusText = strings.status;
    let messageHtml = state?.description ? state.description : strings.message ? strings.message : '';
    let ctaText = strings.cta;
    let ctaDisabled = true;

    if (state?.type === 'Spend X, see message') {
      ctaText = state?.page?.triggerText ? state?.page?.triggerText : ctaText;
    }

    switch (giftState) {
      case 'GROWING':
        if (els.fill) /** @type {HTMLElement} */ (els.fill).style.width = pct + '%';
        break;
      case 'ELIGIBLE':
        ctaDisabled = false;
        messageHtml = '';
        if (els.fill) /** @type {HTMLElement} */ (els.fill).style.width = '100%';
        break;
      case 'CLAIMED':
        ctaDisabled = false;
        messageHtml = '';
        if (els.fill) /** @type {HTMLElement} */ (els.fill).style.width = '100%';
        break;
      case 'UNAVAILABLE':
        els.container.hidden = true; // @IDEA maybe add a toggle for this at metaobject-level
        if (els.fill) /** @type {HTMLElement} */ (els.fill).style.width = '100%';
        break;
      default:
        if (els.fill) /** @type {HTMLElement} */ (els.fill).style.width = pct + '%';
        break;
    }

    messageHtml = messageHtml
      .replace('{remainingSpend}', formatMoney(remaining, currency, false))
      .replace('{currentSpend}', formatMoney(current, currency, false))
      .replace('{targetSpend}', formatMoney(required, currency, false));

    if (els.status) els.status.textContent = statusText;
    if (els.message) els.message.innerHTML = messageHtml;
    if (els.title) els.title.textContent = headerTitle;
    if (els.cta) {
      /** @type {HTMLButtonElement} */ (els.cta).disabled = !!ctaDisabled;
      els.cta.setAttribute('aria-disabled', String(!!ctaDisabled));
      els.cta.textContent = ctaText;
    }
    if (els.required) {
      if (state?.requiredSpend < 1) {
        els.required.textContent = ``;
      } else {
        els.required.textContent = `${formatMoney(required, currency, false)} required spend`;
      }
    }

    // Carousel rendering (only for GWP product type)
    const shouldShowCarousel =
      window?.gwpSettings.showCarousel === true && promotionType === 'Spend X, choose a product (GWP)';

    if (els.products && els.products.parentElement) {
      // Hide the carousel container for non-GWP types
      els.products.parentElement.style.display = shouldShowCarousel ? '' : 'none';
    }

    if (shouldShowCarousel && els.products) {
      let products = [];

      if (giftState === 'CLAIMED' && state && state.selectedGift) {
        // For claimed state, find the selected gift product from giftProducts.
        const selectedGift = state.selectedGift;
        const selectedProduct = Array.isArray(state.giftProducts)
          ? state.giftProducts.find((p) => p.id === selectedGift.productId)
          : null;

        if (selectedProduct) {
          products = [selectedProduct];
        }
      } else {
        // For other states, show all available gift products.
        products = Array.isArray(state && state.giftProducts) ? state.giftProducts : [];
      }

      els.products.innerHTML = products
        .map(function (p) {
          const first = (p.variants || [])[0] || {};
          const price = typeof first.price === 'number' ? formatMoney(first.price, first.currencyCode || currency) : '';
          const compareAtRaw =
            typeof first.compareAtPrice === 'number'
              ? first.compareAtPrice
              : typeof first.compare_at_price === 'number'
                ? first.compare_at_price
                : 0;
          const hasWorth = Number(compareAtRaw) > 0;
          const worthInline = hasWorth
            ? ' <span class="gwp-incart__worth">Worth ' +
              formatMoney(compareAtRaw, first.currencyCode || currency) +
              '</span>'
            : '';
          const img = p.image || placeholder;
          const title = escapeHtml(p.title || 'Gift');
          return (
            '<swiper-slide class="gwp-incart__slide" role="listitem">' +
            '<div class="gwp-incart__product">' +
            '<div class="gwp-incart__media">' +
            '<img src="' +
            img +
            '" alt="' +
            title +
            '" loading="lazy">' +
            '</div>' +
            '<div class="gwp-incart__product-content">' +
            '<h3 class="gwp-incart__product-title">' +
            title +
            '</h3>' +
            '<div class="gwp-incart__price">' +
            (price === 'FREE' ? 'Free' : price) +
            worthInline +
            '</div>' +
            '</div>' +
            '</div>' +
            '</swiper-slide>'
          );
        })
        .join('');

      // Hide or show navigation arrows based on state and product count.
      try {
        const count = Array.isArray(products) ? products.length : 0;
        const shouldShowArrows = count > 1 && giftState !== 'CLAIMED';
        const prevEl = /** @type {HTMLElement} */ (els.prev);
        const nextEl = /** @type {HTMLElement} */ (els.next);
        if (prevEl) {
          prevEl.style.display = shouldShowArrows ? '' : 'none';
          prevEl.setAttribute('aria-hidden', String(!shouldShowArrows));
        }
        if (nextEl) {
          nextEl.style.display = shouldShowArrows ? '' : 'none';
          nextEl.setAttribute('aria-hidden', String(!shouldShowArrows));
        }
      } catch {}

      // Attempt to update or initialize Swiper if present.
      try {
        const swiperEl = /** @type {any} */ (els.products);
        if (swiperEl) {
          const update = () => {
            if (typeof swiperEl.swiper?.update === 'function') {
              swiperEl.swiper.update();
              syncArrows(swiperEl.swiper);
            } else if (typeof swiperEl.initialize === 'function') {
              swiperEl.initialize();
              if (swiperEl.swiper) syncArrows(swiperEl.swiper);
            }
          };
          update();
          // Bind swiper instance events once and re-sync on changes.
          const s = swiperEl.swiper;
          if (s && !(/** @type {HTMLElement} */ (swiperEl).dataset.gwpBound)) {
            try {
              s.on('init', () => syncArrows(s));
              s.on('afterInit', () => syncArrows(s));
              s.on('slideChange', () => syncArrows(s));
              s.on('reachEnd', () => syncArrows(s));
              s.on('reachBeginning', () => syncArrows(s));
              s.on('update', () => syncArrows(s));
              s.on('resize', () => syncArrows(s));
            } catch {}
            /** @type {HTMLElement} */ (swiperEl).dataset.gwpBound = 'true';
          }
          // Fallback DOM event.
          swiperEl.addEventListener(
            'slidechange',
            () => {
              try {
                if (swiperEl.swiper) syncArrows(swiperEl.swiper);
              } catch {}
            },
            { passive: true }
          );
          // Wire buttons.
          if (els.prev && !(/** @type {HTMLElement} */ (els.prev).dataset.bound)) {
            els.prev.addEventListener('click', () => {
              try {
                swiperEl.swiper?.slidePrev?.();
              } catch {}
            });
            /** @type {HTMLElement} */ (els.prev).dataset.bound = 'true';
          }
          if (els.next && !(/** @type {HTMLElement} */ (els.next).dataset.bound)) {
            els.next.addEventListener('click', () => {
              try {
                swiperEl.swiper?.slideNext?.();
              } catch {}
            });
            /** @type {HTMLElement} */ (els.next).dataset.bound = 'true';
          }
        }
      } catch {}
    }
  }

  // Initialize accordion state from localStorage only if not already set by inline script.
  const hasInlineState =
    els.container?.classList.contains('gwp-incart--expanded') ||
    els.container?.classList.contains('gwp-incart--collapsed');
  if (!hasInlineState) {
    const initialAccordionState = getAccordionState();
    updateAccordionState(initialAccordionState);
  }

  // Set up accordion toggle event listener.
  if (els.accordionToggle) {
    els.accordionToggle.addEventListener('click', toggleAccordion);
  }

  // Init: render immediately if state is present, else await a refetch then render.
  (async function init() {
    const initial = getSnapshot();
    if (initial) {
      render(initial);
    } else {
      try {
        const fg = /** @type {any} */ (window).freeGift;
        if (fg && typeof fg.refetch === 'function') {
          await fg.refetch({ awaitUpdate: true, timeoutMs: 8000 });
        }
      } catch {}
      render(getSnapshot());
    }
  })();

  // Global events update this container in place.
  const onUpdate = () => {
    render(getSnapshot());
  };
  window.addEventListener('gwp:event', onUpdate);
  window.addEventListener('gwp:ready', onUpdate);
  window.addEventListener('cart:changed', () => {
    try {
      /** @type {any} */ (window).freeGift?.refetch?.({ awaitUpdate: false });
    } catch {}
  });

  // CTA - Handle different promotion types
  if (els.cta) {
    els.cta.addEventListener('click', () => {
      const state = getSnapshot();
      const promotionType = state && state.type;
      const giftState = state && state.giftState;

      // Only proceed if eligible or claimed
      if (giftState !== 'ELIGIBLE' && giftState !== 'CLAIMED') {
        return;
      }

      if (promotionType === 'Spend X, see collection link(s)' && Array.isArray(state?.collections)) {
        if (state.collections.length === 1) {
          window.location = `/collections/${state.collections[0].handle}`;
          return;
        }
      }

      window?.freeGift?.openModal();
    });
  }
}

/**
 * Hydrate all current and future .gwp-incart nodes.
 */
function setupIncartHydration() {
  const hydrateExisting = () => {
    document.querySelectorAll('.gwp-incart').forEach((el) => hydrateIncart(/** @type {HTMLElement} */ (el)));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrateExisting, { once: true });
  } else {
    hydrateExisting();
  }

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (!m.addedNodes || m.addedNodes.length === 0) continue;
      m.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches && node.matches('.gwp-incart')) {
          hydrateIncart(node);
          return;
        }
        const nested = node.querySelectorAll ? node.querySelectorAll('.gwp-incart') : [];
        if (nested && nested.length) {
          nested.forEach((el) => hydrateIncart(/** @type {HTMLElement} */ (el)));
        }
      });
    }
  });
  observer.observe(document.documentElement || document.body, { childList: true, subtree: true });
}

// Bootstrap.
setupIncartHydration();
